import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import {
  Devices as DevicesIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Cable as CableIcon,
  Router as RouterIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from '../api/axios';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

function Analytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/reports/analytics/dashboard');
      setData(response.data);
    } catch (err) {
      setError('Ошибка загрузки аналитики: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error || 'Нет данных'}</Alert>
      </Container>
    );
  }

  const { summary, devices_by_type, devices_by_location, subnet_utilization } = data;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Аналитика и статистика
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DevicesIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Устройства
                </Typography>
              </Box>
              <Typography variant="h3">{summary.total_devices}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CategoryIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Типы устройств
                </Typography>
              </Box>
              <Typography variant="h3">{summary.total_device_types}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Локации
                </Typography>
              </Box>
              <Typography variant="h3">{summary.total_locations}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CableIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Подключения
                </Typography>
              </Box>
              <Typography variant="h3">{summary.total_connections}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <RouterIcon sx={{ mr: 1, color: '#9c27b0' }} />
                <Typography variant="h6" color="text.secondary">
                  Подсети
                </Typography>
              </Box>
              <Typography variant="h3">{summary.total_subnets}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: summary.incomplete_devices > 0 ? '#fff3e0' : 'inherit' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Незаполненные
                </Typography>
              </Box>
              <Typography variant="h3">{summary.incomplete_devices}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: summary.ip_conflicts > 0 ? '#ffebee' : 'inherit' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  IP конфликты
                </Typography>
              </Box>
              <Typography variant="h3">{summary.ip_conflicts}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1: Devices by Type and Location */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Устройства по типам
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={devices_by_type}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {devices_by_type.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {devices_by_type.map((item, index) => (
                  <Chip
                    key={index}
                    label={`${item.name}: ${item.count}`}
                    size="small"
                    sx={{
                      m: 0.5,
                      bgcolor: COLORS[index % COLORS.length],
                      color: 'white'
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Устройства по локациям (Top 20)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={devices_by_location}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Subnet Utilization */}
      {subnet_utilization && subnet_utilization.length > 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Использование IP адресов в глобальных подсетях
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={subnet_utilization}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="assigned_ips" name="Назначено" stackId="a" fill="#00C49F" />
                    <Bar dataKey="reserved_ips" name="Зарезервировано" stackId="a" fill="#FFBB28" />
                    <Bar dataKey="free_ips" name="Свободно" stackId="a" fill="#e0e0e0" />
                  </BarChart>
                </ResponsiveContainer>

                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    {subnet_utilization.map((subnet, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              {subnet.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Диапазон: {subnet.range_size} адресов
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Использовано: {subnet.total_ips} ({subnet.utilization_percent}%)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={`Назначено: ${subnet.assigned_ips}`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                              <Chip
                                label={`Резерв: ${subnet.reserved_ips}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                              <Chip
                                label={`Свободно: ${subnet.free_ips}`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default Analytics;
